export class FartPlayerAgent {
  // ===== PRIVATE STATE (replaces useState) =====
  private _isPlaying = false;
  private _currentFart: number | null = null;
  private _isLoading = true;
  private _error: string | null = null;
  private _fartCount = 0;
  private _audioData: Uint8Array | null = null;
  private playbackMode: "random" | "single-repeat" = "random";
  private singleFartId: number | null = null;

  // ===== PRIVATE REFERENCES (replaces useRef) =====
  private audioElement: HTMLAudioElement | null = null;
  private recentFarts: number[] = [];
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private source: MediaElementAudioSourceNode | null = null;
  private animationFrameId: number | null = null;

  // ===== SUBSCRIBER MANAGEMENT =====
  private listeners = new Set<() => void>();

  // ===== PUBLIC INTERFACE (getters replace hook return) =====
  get isPlaying() {
    return this._isPlaying;
  }
  get currentFart() {
    return this._currentFart;
  }
  get isLoading() {
    return this._isLoading;
  }
  get error() {
    return this._error;
  }
  get audioData() {
    return this._audioData;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notifyListeners() {
    this.listeners.forEach((fn) => fn());
  }

  // ===== INITIALIZATION (replaces useEffect) =====
  async initialize() {
    // Create audio element
    this.audioElement = new Audio();
    this.audioElement.crossOrigin = "anonymous";

    // Set up Web Audio API
    try {
      const AudioContextClass =
        window.AudioContext ||
        (window as typeof window & { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      this.audioContext = new AudioContextClass();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.8;

      this.source = this.audioContext.createMediaElementSource(
        this.audioElement,
      );
      this.source.connect(this.analyser);
      this.analyser.connect(this.audioContext.destination);

      const bufferLength = this.analyser.frequencyBinCount;
      this._audioData = new Uint8Array(bufferLength);
    } catch (err) {
      console.error("Failed to initialize audio analysis:", err);
    }

    // Fetch fart count
    try {
      const response = await fetch("/api/farts");
      if (!response.ok) {
        throw new Error("Failed to fetch fart list");
      }
      const data = await response.json();
      this._fartCount = data.count;
      this._isLoading = false;

      // Determine playback mode based on domain
      this.playbackMode = this.getPlaybackMode();

      // If single-repeat mode, choose the one fart now
      if (this.playbackMode === "single-repeat" && this._fartCount > 0) {
        const randomArray = new Uint32Array(1);
        crypto.getRandomValues(randomArray);
        this.singleFartId = randomArray[0] % this._fartCount;

        console.log(`Single-repeat mode: locked to fart #${this.singleFartId}`);
      }
    } catch (err) {
      this._error = err instanceof Error ? err.message : "Unknown error";
      this._isLoading = false;
    }

    // Attach event listeners
    this.audioElement.addEventListener("ended", this.handleEnded);

    this.notifyListeners();
  }

  // ===== CLEANUP (replaces useEffect cleanup) =====
  destroy() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
    }
    if (this.audioContext) {
      this.audioContext.close();
    }
    if (this.audioElement) {
      this.audioElement.pause();
      this.audioElement.removeEventListener("ended", this.handleEnded);
      this.audioElement = null;
    }
    this.listeners.clear();
  }

  // ===== PRIVATE HELPER METHODS =====
  /**
   * Determines playback mode based on hostname
   * - randomfart.com (singular) -> single-repeat mode
   * - randomfarts.com (plural) or any other -> random mode
   */
  private getPlaybackMode(): "random" | "single-repeat" {
    const hostname = window.location.hostname.toLowerCase();

    // Match randomfart.com but NOT randomfarts.com
    const isSingularDomain =
      hostname === "randomfart.com" || hostname === "www.randomfart.com";

    return isSingularDomain ? "single-repeat" : "random";
  }

  private getRandomFartId(): number {
    if (this._fartCount === 0) return 0;

    // SINGLE-REPEAT MODE: Always return the same fart
    if (this.playbackMode === "single-repeat" && this.singleFartId !== null) {
      return this.singleFartId;
    }

    // RANDOM MODE: Existing smart random selection logic
    const maxHistory = Math.min(3, Math.floor(this._fartCount / 2));
    let fartId: number;
    let attempts = 0;
    const maxAttempts = 20;

    do {
      const randomArray = new Uint32Array(1);
      crypto.getRandomValues(randomArray);
      fartId = randomArray[0] % this._fartCount;
      attempts++;
    } while (
      this.recentFarts.includes(fartId) &&
      attempts < maxAttempts &&
      this.recentFarts.length < this._fartCount
    );

    this.recentFarts = [...this.recentFarts, fartId].slice(-maxHistory);
    return fartId;
  }

  private playNextFart() {
    if (!this.audioElement || this._fartCount === 0) return;

    const nextFartId = this.getRandomFartId();
    this._currentFart = nextFartId;

    this.audioElement.src = `/api/random?id=${nextFartId}`;
    this.audioElement
      .play()
      .catch((err) => console.error("Error playing audio:", err));

    this.notifyListeners();
  }

  private handleEnded = () => {
    if (this._isPlaying) {
      if (this.playbackMode === "single-repeat") {
        // Wait 2 seconds before repeating the same fart
        setTimeout(() => {
          if (this._isPlaying) {
            this.playNextFart();
          }
        }, 2000);
      } else {
        this.playNextFart();
      }
    }
  };

  private updateAudioData = () => {
    if (this.analyser && this._audioData) {
      this.analyser.getByteFrequencyData(this._audioData);
      this.notifyListeners();
    }
    this.animationFrameId = requestAnimationFrame(this.updateAudioData);
  };

  private startAudioAnalysis() {
    if (!this._isPlaying || !this.analyser || !this._audioData) return;
    this.updateAudioData();
  }

  private stopAudioAnalysis() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  // ===== PUBLIC ACTIONS =====
  togglePlayback() {
    if (!this.audioElement || this._fartCount === 0) return;

    // Resume audio context if suspended
    if (this.audioContext?.state === "suspended") {
      this.audioContext.resume();
    }

    if (this._isPlaying) {
      this.audioElement.pause();
      this._isPlaying = false;
      this.stopAudioAnalysis();
    } else {
      this._isPlaying = true;
      this.playNextFart();
      this.startAudioAnalysis();
    }

    this.notifyListeners();
  }
}
